import {
  getAdditionalUserInfo,
  updateProfile,
  type UserCredential,
} from "firebase/auth";

export const AUTH_PROVIDER_PROFILE_UPDATED_EVENT =
  "auth:providerProfileUpdated";

export type AuthProviderProfileUpdatedDetail = {
  uid: string;
  photoURL: string;
};

const getFirstNonEmptyString = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (typeof value !== "string") continue;
    const normalized = value.trim();
    if (normalized) return normalized;
  }
  return null;
};

export function getProviderProfilePhotoURL(profile: unknown): string | null {
  if (!profile || typeof profile !== "object") return null;
  const value = profile as Record<string, unknown>;
  return getFirstNonEmptyString(
    value.picture,
    value.photoURL,
    value.photo_url,
    value.avatar_url,
  );
}

export function resolveAuthProfilePhotoURL({
  credentialPhotoURL,
  firebasePhotoURL,
  providerPhotoURLs = [],
  serverPhotoURL,
}: {
  credentialPhotoURL?: string | null;
  firebasePhotoURL?: string | null;
  providerPhotoURLs?: Array<string | null | undefined>;
  serverPhotoURL?: string | null;
}): string | null {
  // A credential profile is produced by the current OAuth login and is newer
  // than Firebase Auth's cached user record or the server-side profile row.
  return getFirstNonEmptyString(
    credentialPhotoURL,
    firebasePhotoURL,
    ...providerPhotoURLs,
    serverPhotoURL,
  );
}

export async function syncAuthProviderProfileFromCredential(
  result: UserCredential,
): Promise<string | null> {
  const photoURL = getProviderProfilePhotoURL(
    getAdditionalUserInfo(result)?.profile,
  );
  if (!photoURL) return null;

  if (result.user.photoURL?.trim() !== photoURL) {
    await updateProfile(result.user, { photoURL });
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent<AuthProviderProfileUpdatedDetail>(
        AUTH_PROVIDER_PROFILE_UPDATED_EVENT,
        { detail: { uid: result.user.uid, photoURL } },
      ),
    );
  }

  return photoURL;
}
